/**
 * Plaid Health Board Page
 *
 * Ops board showing all PlaidTokens in LOGIN_REQUIRED or UNKNOWN status,
 * grouped by tenant, with a quick "Send Reconnect Email" action per tenant.
 *
 * @fileoverview Global Plaid health ops board
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Skeleton,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Divider,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import {
  MdRefresh,
  MdCheckCircle,
  MdWarning,
  MdEmail,
  MdBusiness,
  MdAccountBalance,
  MdOpenInNew,
} from 'react-icons/md';
import { usePlaidHealthBoard, useSendReconnectEmail } from '../../hooks/usePlaidHealth';
import { TenantHealthEntry, UnhealthyTokenEntry } from '../../types/admin';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}

// ─── sub-components ──────────────────────────────────────────────────────────

interface TokenRowProps {
  token: UnhealthyTokenEntry;
}

const TokenRow: React.FC<TokenRowProps> = ({ token }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <HStack spacing={3} py={1}>
      <Icon as={MdAccountBalance} color={mutedColor} boxSize={4} />
      <Text fontSize="sm" flex={1}>
        {token.institutionName ?? token.institutionId ?? 'Unknown institution'}
      </Text>
      <Badge
        colorScheme={token.status === 'LOGIN_REQUIRED' ? 'orange' : 'yellow'}
        variant="subtle"
        fontSize="xs"
      >
        {token.status === 'LOGIN_REQUIRED' ? 'Login required' : 'Unknown'}
      </Badge>
      <Tooltip label={new Date(token.updatedAt).toLocaleString()}>
        <Text fontSize="xs" color={mutedColor} whiteSpace="nowrap">
          {formatRelativeTime(token.updatedAt)}
        </Text>
      </Tooltip>
    </HStack>
  );
};

interface TenantCardProps {
  entry: TenantHealthEntry;
  isSending: boolean;
  emailResult: { success: boolean; message: string } | null;
  onSendEmail: () => void;
  onNavigate: () => void;
}

const TenantCard: React.FC<TenantCardProps> = ({
  entry,
  isSending,
  emailResult,
  onSendEmail,
  onNavigate,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const warnBorder = useColorModeValue('orange.200', 'orange.700');

  const hasEmail = Boolean(entry.managerEmail);

  return (
    <Box
      bg={bgColor}
      border="1px"
      borderColor={entry.tokens.some((t) => t.status === 'LOGIN_REQUIRED') ? warnBorder : borderColor}
      borderRadius="lg"
      p={4}
      shadow="sm"
    >
      {/* Tenant header */}
      <HStack justify="space-between" mb={3} wrap="wrap" gap={2}>
        <HStack spacing={2} flex={1} minW={0}>
          <Icon as={MdBusiness} color="orange.400" boxSize={5} flexShrink={0} />
          <VStack align="start" spacing={0} minW={0}>
            <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
              {entry.tenantName}
            </Text>
            <Text fontSize="xs" color={mutedColor} fontFamily="mono">
              {entry.tenantSlug}
            </Text>
          </VStack>
        </HStack>

        <HStack spacing={2} flexShrink={0}>
          {!entry.isActive && (
            <Badge colorScheme="red" variant="subtle" fontSize="xs">
              Inactive
            </Badge>
          )}
          <Badge colorScheme="orange" variant="subtle" fontSize="xs">
            {entry.tokens.length} issue{entry.tokens.length !== 1 ? 's' : ''}
          </Badge>
        </HStack>
      </HStack>

      {/* Tokens */}
      <VStack align="stretch" spacing={1} mb={3}>
        {entry.tokens.map((token) => (
          <TokenRow key={token.id} token={token} />
        ))}
      </VStack>

      <Divider mb={3} />

      {/* Manager email */}
      <Text fontSize="xs" color={mutedColor} mb={3} noOfLines={1}>
        {hasEmail ? `Manager: ${entry.managerEmail}` : 'No manager email configured'}
      </Text>

      {/* Email feedback */}
      {emailResult && (
        <Alert
          status={emailResult.success ? 'success' : 'error'}
          size="sm"
          borderRadius="md"
          mb={3}
          py={2}
          fontSize="xs"
        >
          <AlertIcon boxSize={3} />
          {emailResult.message}
        </Alert>
      )}

      {/* Actions */}
      <HStack spacing={2}>
        <Tooltip
          label={hasEmail ? undefined : 'No manager email configured'}
          isDisabled={hasEmail}
        >
          <Button
            size="sm"
            colorScheme="orange"
            leftIcon={<Icon as={MdEmail} />}
            isLoading={isSending}
            isDisabled={!hasEmail}
            onClick={onSendEmail}
            flex={1}
          >
            Send reconnect email
          </Button>
        </Tooltip>

        <Tooltip label="View tenant">
          <Button
            size="sm"
            variant="ghost"
            onClick={onNavigate}
            aria-label="View tenant"
          >
            <Icon as={MdOpenInNew} />
          </Button>
        </Tooltip>
      </HStack>
    </Box>
  );
};

// ─── main page ────────────────────────────────────────────────────────────────

export const PlaidHealthBoard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, error, refetch } = usePlaidHealthBoard();
  const { sending, results, sendEmail } = useSendReconnectEmail();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const handleSendEmail = async (tenantId: string, tenantName: string) => {
    const result = await sendEmail(tenantId);
    if (result.success) {
      toast({
        title: 'Email sent',
        description: `Reconnection email sent for ${tenantName}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Failed to send email',
        description: result.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <HStack spacing={2} mb={1}>
            <Icon as={MdWarning} color="orange.400" boxSize={6} />
            <Heading size="lg">Plaid Health Board</Heading>
          </HStack>
          <Text color={mutedColor} fontSize="sm">
            Tenants with disconnected bank connections requiring attention
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={MdRefresh} />}
          variant="outline"
          size="sm"
          onClick={refetch}
          isLoading={loading}
        >
          Refresh
        </Button>
      </HStack>

      {/* Error */}
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle>Failed to load health board</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary stats */}
      {!loading && data && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {[
            {
              label: 'Tenants with issues',
              value: data.totalUnhealthyTenants,
              color: data.totalUnhealthyTenants > 0 ? 'orange.500' : 'green.500',
            },
            {
              label: 'Unhealthy connections',
              value: data.totalUnhealthyTokens,
              color: data.totalUnhealthyTokens > 0 ? 'orange.500' : 'green.500',
            },
            {
              label: 'Login required',
              value: data.entries.reduce(
                (sum, e) => sum + e.tokens.filter((t) => t.status === 'LOGIN_REQUIRED').length,
                0
              ),
              color: 'orange.500',
            },
            {
              label: 'Unknown status',
              value: data.entries.reduce(
                (sum, e) => sum + e.tokens.filter((t) => t.status === 'UNKNOWN').length,
                0
              ),
              color: 'yellow.500',
            },
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              bg={bgColor}
              border="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
              textAlign="center"
            >
              <Text fontSize="2xl" fontWeight="bold" color={color}>
                {value}
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                {label}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Skeleton */}
      {loading && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} h="200px" borderRadius="lg" />
          ))}
        </SimpleGrid>
      )}

      {/* All healthy */}
      {!loading && data && data.entries.length === 0 && (
        <Box
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={10}
          textAlign="center"
        >
          <Icon as={MdCheckCircle} color="green.400" boxSize={12} mb={3} />
          <Heading size="md" mb={2}>
            All systems healthy
          </Heading>
          <Text color={mutedColor} fontSize="sm">
            No tenants with disconnected bank connections.
          </Text>
          {data.lastChecked && (
            <Text fontSize="xs" color={mutedColor} mt={2}>
              Last checked: {new Date(data.lastChecked).toLocaleTimeString()}
            </Text>
          )}
        </Box>
      )}

      {/* Tenant cards */}
      {!loading && data && data.entries.length > 0 && (
        <>
          <HStack justify="space-between">
            <Text fontSize="sm" color={mutedColor}>
              {data.entries.length} tenant{data.entries.length !== 1 ? 's' : ''} need attention
            </Text>
            {data.lastChecked && (
              <Text fontSize="xs" color={mutedColor}>
                Last checked: {new Date(data.lastChecked).toLocaleTimeString()}
              </Text>
            )}
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {data.entries.map((entry) => (
              <TenantCard
                key={entry.tenantId}
                entry={entry}
                isSending={sending[entry.tenantId] ?? false}
                emailResult={results[entry.tenantId] ?? null}
                onSendEmail={() => handleSendEmail(entry.tenantId, entry.tenantName)}
                onNavigate={() => navigate(`/admin/tenants/${entry.tenantId}`)}
              />
            ))}
          </SimpleGrid>
        </>
      )}
    </VStack>
  );
};

export default PlaidHealthBoard;
